import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const DATA_URL_PREFIX = 'data:image';
const COMPANY_UPLOAD_PATH = 'uploads/company/';
const isDataUrl = (value: string) => value.startsWith(DATA_URL_PREFIX);
const isAbsoluteUrl = (value: string) => /^https?:\/\//i.test(value);

export const useCompanyLogo = () => {
  const [logoTimestamp, setLogoTimestamp] = useState(Date.now());
  const [logoSource, setLogoSource] = useState<string>('logo.png');
  const [logoDarkSource, setLogoDarkSource] = useState<string>('');

  const applyLogoValue = useCallback((logoValue?: string | null) => {
    if (typeof logoValue !== 'string') {
      setLogoSource('logo.png');
      return;
    }

    const trimmedValue = logoValue.trim();
    if (!trimmedValue) {
      setLogoSource('logo.png');
      return;
    }

    if (isDataUrl(trimmedValue) || isAbsoluteUrl(trimmedValue)) {
      setLogoSource(trimmedValue);
      return;
    }

    const normalizedFilename = trimmedValue.replace(
      new RegExp(`^/?${COMPANY_UPLOAD_PATH}`, 'i'),
      ''
    );
    setLogoSource(normalizedFilename || 'logo.png');
  }, []);

  const applyLogoDarkValue = useCallback((logoValue?: string | null) => {
    if (typeof logoValue !== 'string') {
      setLogoDarkSource('');
      return;
    }

    const trimmedValue = logoValue.trim();
    if (!trimmedValue) {
      setLogoDarkSource('');
      return;
    }

    if (isDataUrl(trimmedValue) || isAbsoluteUrl(trimmedValue)) {
      setLogoDarkSource(trimmedValue);
      return;
    }

    const normalizedFilename = trimmedValue.replace(
      new RegExp(`^/?${COMPANY_UPLOAD_PATH}`, 'i'),
      ''
    );
    setLogoDarkSource(normalizedFilename || '');
  }, []);

  useEffect(() => {
    const fetchLogoInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const endpoint = token ? '/api/settings/company' : '/api/settings/company/public';
        const response = await axios.get(endpoint);
        applyLogoValue(response.data?.logo);
        applyLogoDarkValue(response.data?.logoDark);
      } catch (error) {
        console.error('Error cargando informacion del logo:', error);
        if (axios.isAxiosError(error) && (error.response?.status === 401 || error.response?.status === 403)) {
          try {
            const publicResponse = await axios.get('/api/settings/company/public');
            applyLogoValue(publicResponse.data?.logo);
            applyLogoDarkValue(publicResponse.data?.logoDark);
          } catch (publicError) {
            console.error('Error cargando informacion publica del logo:', publicError);
          }
        }
      }
    };

    fetchLogoInfo();
  }, [applyLogoValue, applyLogoDarkValue]);

  useEffect(() => {
    const handleLogoUpdate = (event: any) => {
      setLogoTimestamp(Date.now());
      applyLogoValue(event.detail?.logoPath);
    };

    const handleLogoDarkUpdate = (event: any) => {
      setLogoTimestamp(Date.now());
      applyLogoDarkValue(event.detail?.logoPath);
    };

    window.addEventListener('company-logo-updated', handleLogoUpdate);
    window.addEventListener('company-logo-dark-updated', handleLogoDarkUpdate);
    return () => {
      window.removeEventListener('company-logo-updated', handleLogoUpdate);
      window.removeEventListener('company-logo-dark-updated', handleLogoDarkUpdate);
    };
  }, [applyLogoValue, applyLogoDarkValue]);

  const buildLogoUrl = (source: string, fallback: string) => {
    if (!source) {
      return fallback;
    }

    if (isDataUrl(source) || isAbsoluteUrl(source)) {
      return source;
    }

    const apiBase = ((process.env.REACT_APP_API_URL || '') || window.location.origin).replace(/\/$/, '');
    const sanitizedFilename = source.replace(
      new RegExp(`^/?${COMPANY_UPLOAD_PATH}`, 'i'),
      ''
    );
    return `${apiBase}/${COMPANY_UPLOAD_PATH}${sanitizedFilename}?ts=${logoTimestamp}`;
  };

  const getLogoUrl = (fallback: string = '/rocam-new-logo-2025.png') => {
    return buildLogoUrl(logoSource, fallback);
  };

  const getLogoDarkUrl = (fallback?: string) => {
    if (!logoDarkSource) {
      return getLogoUrl(fallback);
    }
    return buildLogoUrl(logoDarkSource, getLogoUrl(fallback));
  };

  return { logoTimestamp, getLogoUrl, getLogoDarkUrl };
};
